import { ClientInfo } from './components/CoachInfo';
import { useCallback, useEffect, useState } from 'react';
import * as api from './lib/api';
import type { Me } from './lib/api';
import { getToken } from './lib/api';
import { initTelegram, inTelegram, startParam, tg } from './lib/telegram';
import { useNavBack } from './lib/nav';
import { CoachSignup } from './screens/CoachSignup';
import { BookingClosed, BookScreen, readableError, WaitingForApproval } from './screens/BookScreen';
import { BookingsScreen, CalendarFeedRow, CreditsSection, MatchesSection } from './screens/Screens';
import { CoachScreen, setCoachToken } from './screens/CoachScreen';
import { LanguageSwitch } from './components/LanguageSwitch';
import { HealthConsentCard } from './components/HealthConsentCard';
import { MyGoal, MyJournal } from './components/Journal';
import { MyProfile } from './components/MyProfile';
import { asLang, setLang, useT } from './lib/i18n';
import { useKeyboardInset } from './lib/keyboard';
import { useSheetGuard } from './lib/sheets';
import { KeyboardDebug, wantsDebug } from './components/KeyboardDebug';
import { startTab, type ClientTab } from './lib/start-tab';

type Tab = ClientTab;

const TABS: { id: Tab; key: 'tab.book' | 'tab.bookings' | 'tab.profile' }[] = [
  { id: 'book', key: 'tab.book' },
  { id: 'bookings', key: 'tab.bookings' },
  { id: 'profile', key: 'tab.profile' },
];

export function App() {
  const t = useT();
  // Sheets with a field in them sit above the keyboard, not under it,
  // and do not throw away what was typed on a tap outside.
  useKeyboardInset();
  useSheetGuard();
  // Telegram's back arrow, swipe-to-close and the closing question, for every screen.
  useNavBack();
  const [me, setMe] = useState<Me | null>(null);
  // A bot button can open a screen: ?tab=profile, or ?tab=journal (My
  // profile, scrolled to the journal). Otherwise Book.
  const [start] = useState(() => startTab(window.location.search, startParam()));
  const [tab, setTab] = useState<Tab>(start.tab);
  // Set when the credits line on Book is tapped: My profile scrolls to Credits.
  const [creditsFocus, setCreditsFocus] = useState<number | undefined>(undefined);
  const [journalFocus] = useState<number | undefined>(() => (start.journal ? Date.now() : undefined));
  const [fatal, setFatal] = useState<string | null>(null);

  // t.me/<bot>?start=coach → the bot's button opens ?signup=1: the sign-up
  // screen, not a sign-in (which would file the person as a client).
  const [signingUp, setSigningUp] = useState(() =>
    new URLSearchParams(window.location.search).get('signup') === '1' || startParam()?.toLowerCase() === 'coach');

  useEffect(() => {
    initTelegram();
    if (signingUp) return;
    api
      .authenticate()
      .then((m) => {
        // The server decides the starting language — from their own
        // setting, or from Telegram's locale the first time they arrive.
        document.documentElement.lang = asLang(m.language);
        setLang(asLang(m.language));
        setMe(m);
      })
      .catch((err) => {
        // Nobody to ask the server about, so Telegram's own locale decides.
        setLang(asLang(tg?.initDataUnsafe.user?.language_code));
        setFatal(readableError(err));
      });
  }, [signingUp]);

  const refresh = useCallback(() => {
    api.getMe().then((m) => { setLang(asLang(m.language)); setMe(m); }).catch(() => {});
  }, []);

  if (signingUp) {
    // Already a coach: just their app.
    return <CoachSignup onAlreadyCoach={() => setSigningUp(false)} />;
  }

  if (fatal) {
    return (
      <div className="app">
        <div className="empty">
          <div className="empty__lead">{t('app.signInFailed')}</div>
          {fatal}
          {!inTelegram && (
            <div style={{ marginTop: 14, fontSize: 12.5, opacity: 0.75 }}>
              Running outside Telegram. Set VITE_DEV_INIT_DATA in web/.env.local
              using the output of <code>npm run fake-init</code>.
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="app">
        <div className="list" style={{ paddingTop: 24 }}>
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  if (me.role === 'coach') {
    setCoachToken(getToken());
    return (
      <div className="app">
        <CoachScreen firstName={me.firstName} billing={(me as any).billing ?? null} isOwner={(me as any).isOwner === true} />
        {/* ?debug=1 only: the keyboard's numbers, for a phone screenshot. */}
        {wantsDebug() && <KeyboardDebug />}
      </div>
    );
  }

  const credits = me.credits ?? 0;

  return (
    <div className="app">
      {wantsDebug() && <KeyboardDebug />}
      <LanguageSwitch onChanged={refresh} />
      {/*
        Everyone already on the books is asked here, once. A card above
        their own screens rather than a modal: it is a question, not an
        obstacle, and someone who opened the app to check tomorrow's time
        should still be able to.
      */}
      {me.healthConsent === 'unanswered' && (
        <HealthConsentCard onAnswered={refresh} />
      )}

      <div className="tabs tabs--top" role="tablist">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            role="tab"
            className="tab"
            aria-selected={tab === tb.id}
            onClick={() => setTab(tb.id)}
          >
            {t(tb.key)}
          </button>
        ))}
      </div>

      {tab === 'book' && (me.approved === false
        ? <WaitingForApproval coachName={me.coachName ?? null} />
        : me.coachOff
        // The coach is switched off: nothing new can be booked, sessions already booked stay.
        ? <BookingClosed />
        : <>
            <BookScreen credits={credits} onChanged={refresh}
              firstName={me.firstName} hadPackage={me.hadPackage ?? credits !== 0}
              // The credits pill in the header opens Credits on My profile.
              onCredits={() => { setTab('profile'); setCreditsFocus(Date.now()); }}
              horizonDays={me.bookingHorizonDays} creditOnUse={me.creditOnUse === true} />
          </>)}
      {tab === 'bookings' && <BookingsScreen onChanged={refresh} onBook={() => setTab('book')}
        credits={credits} coverage={me.coverage ?? null} />}
      {/*
        My profile, in order: the goal (when set) and skills, the last four
        weeks, credits, matches, the journal — and the coach's «Инфо» last,
        one row that opens in place (open from the start for a new player).
      */}
      {tab === 'profile' && <><MyGoal /><MyProfile />
        {/* Only for someone who has ever had a package: a pay-per-session player has nothing here. */}
        {(me.hadPackage ?? credits !== 0) && <CreditsSection me={me} focusAt={creditsFocus} />}
        <MatchesSection clientId={me.id} /><MyJournal focusAt={journalFocus} /><CalendarFeedRow /><ClientInfo /></>}
    </div>
  );
}
